/**
 * MentionInline — @-mention inline content for MarthaEditor.
 * Renders as a styled chip (page or user) inside flowing text.
 * Uses createReactInlineContentSpec for inline content.
 */

import { createReactInlineContentSpec } from '@blocknote/react';
import { FileText, User } from 'lucide-react';

// ── Inline content spec ───────────────────────────────────────────────────

export const mentionInlineSpec = createReactInlineContentSpec(
  {
    type: 'mention' as const,
    propSchema: {
      id: { default: '' },
      name: { default: '' },
      kind: { default: 'page' }, // 'page' | 'user'
    },
    content: 'none',
  },
  {
    render: (props) => {
      const { id, name, kind } = props.inlineContent.props as {
        id: string;
        name: string;
        kind: string;
      };

      const isPage = kind === 'page';

      return (
        <span
          className={`mention-chip inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-sm font-medium cursor-pointer
            ${isPage
              ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              : 'bg-purple-50 text-purple-700 hover:bg-purple-100'
            } transition-colors`}
          data-mention-id={id}
          data-mention-kind={kind}
          title={isPage ? `Go to page: ${name}` : `User: ${name}`}
          onClick={(e) => {
            if (isPage && id) {
              e.preventDefault();
              // Navigate to the page — dispatch custom event for the page router
              window.dispatchEvent(
                new CustomEvent('martha:navigate-page', { detail: { pageId: id } })
              );
            }
          }}
          contentEditable={false}
        >
          {isPage ? (
            <FileText className="w-3.5 h-3.5 shrink-0" />
          ) : (
            <User className="w-3.5 h-3.5 shrink-0" />
          )}
          <span>{name || 'Untitled'}</span>
        </span>
      );
    },
  }
);

// ── Mention suggestion item interface ─────────────────────────────────────

export interface MentionSuggestionItem {
  id: string;
  name: string;
  kind: 'page' | 'user';
  icon?: string | null;
  subtitle?: string;
}

export default mentionInlineSpec;
